/**
 * Calcula a parcela pela Tabela Price (Sistema Francês de Amortização).
 * PMT = PV × i / (1 − (1+i)^−n), com i = taxaAm%/100.
 * Se taxa = 0, retorna PV / n.
 */
export function calcularParcelaTabelaPrice(
  valorPresente: number,
  taxaAmPercent: number,
  numParcelas: number,
  maxParcelas = 10
): number {
  const pv = Number(valorPresente) || 0;
  const limite = Math.max(1, Math.floor(Number(maxParcelas) || 10));
  const n = Math.max(1, Math.min(limite, Math.floor(Number(numParcelas) || 1)));
  const taxa = Math.max(0, Number(taxaAmPercent) || 0);

  if (pv <= 0) return 0;
  if (taxa === 0) return pv / n;

  const i = taxa / 100;
  const fator = Math.pow(1 + i, n);
  const pmt = (pv * i * fator) / (fator - 1);
  return Number.isFinite(pmt) ? pmt : 0;
}

export type SavePgtoTipo = "reais" | "percentual";

/** Converte valor digitado (R$ ou %) para reais, dado o total da proposta. */
export function saveValorEtapaEmReais(
  valorDigitado: number,
  tipo: SavePgtoTipo,
  valorTotal: number
): number {
  const v = Math.max(0, Number(valorDigitado) || 0);
  const total = Math.max(0, Number(valorTotal) || 0);
  if (tipo === "percentual") {
    return (total * v) / 100;
  }
  return v;
}

/** Soma das três etapas em reais (para validar ≈ valor total). */
export function saveSomaEtapasEmReais(
  entrada: { valor: number; tipo: SavePgtoTipo },
  inicio: { valor: number; tipo: SavePgtoTipo },
  conclusao: { valor: number; tipo: SavePgtoTipo },
  valorTotal: number
): number {
  return (
    saveValorEtapaEmReais(entrada.valor, entrada.tipo, valorTotal) +
    saveValorEtapaEmReais(inicio.valor, inicio.tipo, valorTotal) +
    saveValorEtapaEmReais(conclusao.valor, conclusao.tipo, valorTotal)
  );
}

export const SAVE_GARANTIA_SERVICO_MESES_PADRAO = 18;
export const SAVE_GARANTIA_CLAMPER_MESES_PADRAO = 60;

export function parseMesesGarantiaSave(value: unknown, padrao: number): number {
  const n = Math.floor(Number(value) || 0);
  return Number.isFinite(n) && n >= 1 ? n : padrao;
}

export function formatarMesesGarantiaSave(meses: number): string {
  const n = Math.max(1, Math.floor(Number(meses) || 0));
  return n === 1 ? "1 mês" : `${n} meses`;
}

/** Tolerância de 1 centavo para validação das etapas. */
export function saveEtapasSomamTotal(
  entrada: { valor: number; tipo: SavePgtoTipo },
  inicio: { valor: number; tipo: SavePgtoTipo },
  conclusao: { valor: number; tipo: SavePgtoTipo },
  valorTotal: number,
  tolerancia = 0.01
): boolean {
  const total = Math.max(0, Number(valorTotal) || 0);
  const soma = saveSomaEtapasEmReais(entrada, inicio, conclusao, total);
  return Math.abs(soma - total) <= tolerancia;
}
